var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@web/components/layout/Button';
import { RangeDatePickerWithPeriod } from '@web/components/layout/DatePicker/RangeDatePicker';
import { Input } from '@web/components/ui/Form/Input';
import Select from '@web/components/ui/Form/Select';
import { useWorkspaceCtx } from '@web/contexts/Workspace';
import { trpc } from '@web/lib/trpc';
import { useFilters } from '@web/utility/hooks/useFilters';
import { useNavigateTo } from '@web/utility/hooks/useNavigateTo';
import { DocumentTypeEnum, DriveEventActionEnum, DriveEventSubActionEnum } from '@workalizer/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
function FilterField(_a) {
    var label = _a.label, children = _a.children;
    return (_jsxs("div", __assign({ className: "flex md:items-center flex-col md:flex-row gap-2 md:gap-5" }, { children: [_jsx("label", __assign({ className: "text-sm font-medium text-gray-700 shrink-0 min-w-[110px]" }, { children: label })), _jsx("div", __assign({ className: "min-w-[290px] w-full" }, { children: children }))] })));
}
export var SearchFilters = function (_a) {
    var _b, _c, _d, _e, _f;
    var filters = _a.filters, onChange = _a.onChange, onSearch = _a.onSearch;
    var t = useTranslation().t;
    var users = trpc.users.useQuery({ isActive: true }).data;
    var orgUnits = useWorkspaceCtx().orgUnits;
    var currentFilters = useFilters();
    var navigateTo = useNavigateTo();
    var location = useLocation();
    var documentTypes = [
        {
            id: null,
            name: t('app.driveAdvancedSearch.filters.documentTypes.any')
        },
        {
            id: DocumentTypeEnum.DOCUMENT,
            name: t('app.driveAdvancedSearch.filters.documentTypes.document')
        },
        {
            id: DocumentTypeEnum.SPREADSHEET,
            name: t('app.driveAdvancedSearch.filters.documentTypes.spreadsheet')
        },
        {
            id: DocumentTypeEnum.PRESENTATION,
            name: t('app.driveAdvancedSearch.filters.documentTypes.presentation')
        },
        {
            id: DocumentTypeEnum.OTHER,
            name: t('app.driveAdvancedSearch.filters.documentTypes.other')
        }
    ];
    var actionTypes = [
        {
            id: null,
            action: null,
            name: t('app.driveAdvancedSearch.filters.actionTypes.any')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_CREATE,
            action: DriveEventActionEnum.DOCUMENT_CREATE,
            name: t('app.driveAdvancedSearch.filters.actionTypes.create')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_EDIT,
            action: DriveEventActionEnum.DOCUMENT_EDIT,
            name: t('app.driveAdvancedSearch.filters.actionTypes.edit')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_UPLOAD,
            action: DriveEventActionEnum.DOCUMENT_UPLOAD,
            name: t('app.driveAdvancedSearch.filters.actionTypes.upload')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_VIEW,
            action: DriveEventActionEnum.DOCUMENT_VIEW,
            name: t('app.driveAdvancedSearch.filters.actionTypes.view')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_PRINT,
            action: DriveEventActionEnum.DOCUMENT_PRINT,
            name: t('app.driveAdvancedSearch.filters.actionTypes.print')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_DOWNLOAD,
            action: DriveEventActionEnum.DOCUMENT_DOWNLOAD,
            name: t('app.driveAdvancedSearch.filters.actionTypes.download')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_COPY,
            action: DriveEventActionEnum.DOCUMENT_COPY,
            name: t('app.driveAdvancedSearch.filters.actionTypes.makeACopy')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_CREATE_COMMENT,
            action: DriveEventActionEnum.DOCUMENT_CREATE_COMMENT,
            name: t('app.driveAdvancedSearch.filters.actionTypes.commentPostResolve')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_RESOLVE_COMMENT,
            action: DriveEventActionEnum.DOCUMENT_RESOLVE_COMMENT,
            name: t('app.driveAdvancedSearch.filters.actionTypes.resolveComment')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_CREATE_SUGGESTION,
            action: DriveEventActionEnum.DOCUMENT_CREATE_SUGGESTION,
            name: t('app.driveAdvancedSearch.filters.actionTypes.suggestionPostResolve')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_ACCEPT_SUGGESTION,
            action: DriveEventActionEnum.DOCUMENT_ACCEPT_SUGGESTION,
            name: t('app.driveAdvancedSearch.filters.actionTypes.acceptSuggestion')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_REJECT_SUGGESTION,
            action: DriveEventActionEnum.DOCUMENT_REJECT_SUGGESTION,
            name: t('app.driveAdvancedSearch.filters.actionTypes.rejectSuggestion')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_APPROVAL_REQUESTED,
            action: DriveEventActionEnum.DOCUMENT_APPROVAL_REQUESTED,
            name: t('app.driveAdvancedSearch.filters.actionTypes.approvalRequest')
        },
        {
            id: "".concat(DriveEventActionEnum.DOCUMENT_SHARE, "-").concat(DriveEventSubActionEnum.DOCUMENT_SHARED_INTERNALLY),
            action: DriveEventActionEnum.DOCUMENT_SHARE,
            subAction: DriveEventSubActionEnum.DOCUMENT_SHARED_INTERNALLY,
            name: t('app.driveAdvancedSearch.filters.actionTypes.shareInternally')
        },
        {
            id: "".concat(DriveEventActionEnum.DOCUMENT_SHARE, "-").concat(DriveEventSubActionEnum.DOCUMENT_SHARED_EXTERNALLY),
            action: DriveEventActionEnum.DOCUMENT_SHARE,
            subAction: DriveEventSubActionEnum.DOCUMENT_SHARED_EXTERNALLY,
            name: t('app.driveAdvancedSearch.filters.actionTypes.shareExternally')
        },
        {
            id: DriveEventActionEnum.DOCUMENT_PUBLISH,
            action: DriveEventActionEnum.DOCUMENT_PUBLISH,
            name: t('app.driveAdvancedSearch.filters.actionTypes.publish')
        }
    ];
    var handleActionChange = function (option) {
        var _a;
        var actionOption = actionTypes.find(function (action) { return action.id === option.id; });
        var newFilters = __assign(__assign({}, filters), { driveSearchAction: (actionOption === null || actionOption === void 0 ? void 0 : actionOption.action) === null ||
                actionOption === null ||
                actionOption === undefined
                ? undefined
                : actionOption.action, driveSearchSubAction: (_a = actionOption === null || actionOption === void 0 ? void 0 : actionOption.subAction) !== null && _a !== void 0 ? _a : ((actionOption === null || actionOption === void 0 ? void 0 : actionOption.action) ? null : undefined) });
        onChange(newFilters);
        navigateTo({
            pathname: location.pathname,
            newFilters: newFilters
        });
    };
    var selectedAction = (_b = actionTypes.find(function (action) {
        if (action.action === null) {
            return !(filters === null || filters === void 0 ? void 0 : filters.driveSearchAction) && !(filters === null || filters === void 0 ? void 0 : filters.driveSearchSubAction);
        }
        if (action.subAction) {
            return ((filters === null || filters === void 0 ? void 0 : filters.driveSearchSubAction) === action.subAction &&
                (!(filters === null || filters === void 0 ? void 0 : filters.driveSearchAction) ||
                    filters.driveSearchAction === action.action));
        }
        return (action.action === (filters === null || filters === void 0 ? void 0 : filters.driveSearchAction) &&
            !(filters === null || filters === void 0 ? void 0 : filters.driveSearchSubAction));
    })) !== null && _b !== void 0 ? _b : null;
    var handleChange = useCallback(function (key, value, extraFilters) {
        var _a;
        var newFilters = __assign(__assign(__assign({}, filters), extraFilters), (_a = {}, _a[key] = key === 'driveSearchSubAction' && value === null
            ? null
            : value === null
                ? undefined
                : value, _a));
        onChange(newFilters);
        navigateTo({
            pathname: location.pathname,
            newFilters: newFilters
        });
    }, [filters, onChange, navigateTo, location.pathname]);
    var handleDateChange = useCallback(function (period, dateFrom, dateTo) {
        onChange(__assign(__assign({}, filters), { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() }));
        var newFilters = __assign(__assign({}, filters), { period: period, dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() });
        if (period !== 'custom') {
            newFilters.hasInitialSearch = null;
        }
        navigateTo({
            pathname: location.pathname,
            newFilters: newFilters
        });
    }, [filters, onChange, navigateTo, location.pathname]);
    var teamOptions = __spreadArray([
        {
            id: null,
            name: t('app.driveAdvancedSearch.filters.teamPlaceholder'),
            isAll: true
        }
    ], orgUnits.map(function (orgUnit) { return ({
        id: orgUnit.id,
        name: orgUnit.title
    }); }), true);
    var selectedTeam = (_c = teamOptions.find(function (team) { return team.id === (filters === null || filters === void 0 ? void 0 : filters.orgUnit); })) !== null && _c !== void 0 ? _c : teamOptions[0];
    var usersInTeam = (filters === null || filters === void 0 ? void 0 : filters.orgUnit)
        ? users === null || users === void 0 ? void 0 : users.filter(function (user) { var _a; return ((_a = user.orgUnit) === null || _a === void 0 ? void 0 : _a.id) === filters.orgUnit; })
        : users;
    var userOptions = __spreadArray([
        {
            id: null,
            name: t('app.driveAdvancedSearch.filters.actionsByPlaceholder'),
            isAll: true
        }
    ], ((_d = usersInTeam === null || usersInTeam === void 0 ? void 0 : usersInTeam.map(function (u) { return ({
        id: u.id,
        name: u.name || u.email
    }); })) !== null && _d !== void 0 ? _d : []), true);
    var selectedUser = (filters === null || filters === void 0 ? void 0 : filters.user)
        ? userOptions.find(function (u) { return u.id === (filters === null || filters === void 0 ? void 0 : filters.user); })
        : null;
    return (_jsxs("div", __assign({ className: "rounded-lg p-4 shadow bg-gray-100" }, { children: [_jsxs("div", __assign({ className: "grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 py-2" }, { children: [_jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.titleId') }, { children: _jsx(Input, { type: "text", value: (_e = filters === null || filters === void 0 ? void 0 : filters.driveSearchTitle) !== null && _e !== void 0 ? _e : '', onChange: function (e) { return handleChange('driveSearchTitle', e.target.value); }, placeholder: t('app.driveAdvancedSearch.filters.titleIdPlaceholder'), className: "w-full" }) })), _jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.type') }, { children: _jsx(Select, { selected: (_f = documentTypes.find(function (t) { return t.id === (filters === null || filters === void 0 ? void 0 : filters.driveSearchType); })) !== null && _f !== void 0 ? _f : null, onChange: function (option) { return handleChange('driveSearchType', option === null || option === void 0 ? void 0 : option.id); }, options: documentTypes, placeholder: t('app.driveAdvancedSearch.filters.typePlaceholder'), className: "w-full" }) })), _jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.team') }, { children: _jsx(Select, { selected: selectedTeam, onChange: function (option) {
                                var _a;
                                var orgUnitId = (_a = option === null || option === void 0 ? void 0 : option.id) !== null && _a !== void 0 ? _a : null;
                                var selectedUserStillInTeam = !(filters === null || filters === void 0 ? void 0 : filters.user) ||
                                    (users === null || users === void 0 ? void 0 : users.some(function (user) {
                                        var _a;
                                        return user.id === filters.user &&
                                            (!orgUnitId || ((_a = user.orgUnit) === null || _a === void 0 ? void 0 : _a.id) === orgUnitId);
                                    }));
                                handleChange('orgUnit', orgUnitId, selectedUserStillInTeam ? undefined : { user: null });
                            }, options: teamOptions, placeholder: t('app.driveAdvancedSearch.filters.teamPlaceholder'), className: "w-full" }) })), _jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.actions') }, { children: _jsx(Select, { selected: selectedAction, onChange: handleActionChange, options: actionTypes, placeholder: t('app.driveAdvancedSearch.filters.actionsPlaceholder'), className: "w-full" }) })), _jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.actionsBy') }, { children: _jsx(Select, { selected: selectedUser, onChange: function (option) { return handleChange('user', option === null || option === void 0 ? void 0 : option.id); }, options: userOptions, placeholder: t('app.driveAdvancedSearch.filters.actionsByPlaceholder'), className: "w-full" }) })), _jsx(FilterField, __assign({ label: t('app.driveAdvancedSearch.filters.datePerformed') }, { children: _jsx(RangeDatePickerWithPeriod, { onChange: handleDateChange, period: currentFilters.period, dateFrom: new Date(currentFilters.dateFrom), dateTo: new Date(currentFilters.dateTo), isDisplayLabel: false }) }))] })), _jsx("div", __assign({ className: "flex items-center justify-center sm:justify-start mt-4" }, { children: _jsx(Button, __assign({ type: "button", variant: "primaryDark", onClick: onSearch }, { children: t('app.driveAdvancedSearch.filters.search') })) }))] })));
};
